import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import CT from '@marketplace-ai/tags';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { HeaderBrandMenu } from '~/components/BrandSwitcher';
import { HlxLogo, McvLogo } from '~/components/logos';
import { useBrandConfig, type SiteBrand } from '~/config/branding';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { paths } from '~/routing/routes';
import { SITE_NAV_ITEMS } from '../siteNavigation';
import { usePublicAuth, type PublicAuthState } from './publicAuth';

const activeLinkClass = 'underline decoration-2 underline-offset-8';

type SignedInAuth = Extract<PublicAuthState, { status: 'signed-in' }>;

/** One letter per leading word of the name, e.g. "Ada Lovelace" -> "AL". */
const initialsOf = (name: string | null): string =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => Array.from(word)[0]?.toUpperCase() ?? '')
    .join('');

/** The visitor's photo, else their initials, else a generic person glyph. Decorative: the name sits beside it. */
const AccountAvatar = ({ displayName, imageUrl }: SignedInAuth) => {
  if (imageUrl) {
    return <img src={imageUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" />;
  }
  const initials = initialsOf(displayName);
  if (initials) {
    return (
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-grey-900 text-xs font-semibold text-grey-100"
      >
        {initials}
      </span>
    );
  }
  return <UserCircleIcon aria-hidden="true" className="size-8 shrink-0 text-grey-600" />;
};

/**
 * Who is signed in, in the desktop bar. Without a name the visible text is just
 * "Signed in".
 *
 * The room beside the tabs is brand-specific and small: MCV has about 168px at
 * `lg` and 216px from `xl`; HLX has about 110px at EVERY width, because its 338px
 * lockup and the tabs fill the bar, which stops growing at max-w-7xl. So MCV shows
 * the avatar always and a name truncated to 56px from `xl`, and HLX shows nothing
 * here. Linux/Windows Chromium draws the tab labels about 11px wider in total than
 * macOS (measured in CI), so the cluster keeps that much slack.
 * The text stays in the DOM, screen-reader only, wherever it is not drawn. The
 * drawer has the room and always shows it in full.
 */
const AccountIdentity = ({ auth, brand }: { auth: SignedInAuth; brand: SiteBrand }) => {
  const label = auth.displayName ? (
    <>
      <span className="sr-only">Signed in as</span> {auth.displayName}
    </>
  ) : (
    'Signed in'
  );

  // Nothing to draw: skip the avatar too, so a hidden photo is never fetched.
  if (brand === 'hlx') return <span className="sr-only body-text">{label}</span>;

  return (
    <span className="flex items-center gap-x-2 body-text">
      <AccountAvatar {...auth} />
      <span className="min-w-0 max-xl:sr-only xl:max-w-14 xl:truncate">{label}</span>
    </span>
  );
};

/** Render public navigation with a drawer breakpoint sized for the active brand's logo. */
export const PublicHeader = () => {
  const { pathname } = useLocation();
  // The legacy Resources URL renders the same knowledge base as FAQ.
  const normalizedPath = pathname.replace(/\/+$/, '').toLowerCase();
  const currentPage = normalizedPath === '/resources' ? '/faq' : normalizedPath;
  const navigationItems = SITE_NAV_ITEMS.map((item) => ({
    ...item,
    isActive: currentPage === item.href,
  }));
  const [collapsedMenuOpen, setCollapsedMenuOpen] = useState<boolean>(false);
  const { brand, siteName } = useBrandConfig();
  // [MCV-1723] Signed-in awareness for the public pages, which a signed-in user
  // reaches from the app footer's legal links (new tab) or by visiting the site
  // directly. Defaults to signed-out where Clerk is absent.
  const auth = usePublicAuth();
  // HLX's 338px lockup needs more room than MCV's 151px lockup. Keep
  // the drawer available until every desktop link fits beside that logo.
  const isLargeScreen = useMediaQuery(
    brand === 'hlx' ? '(min-width: 1280px)' : '(min-width: 1024px)'
  );
  const collapsedMenuClass = brand === 'hlx' ? 'xl:hidden' : 'lg:hidden';
  useEffect(() => {
    if (isLargeScreen) setCollapsedMenuOpen(false);
  }, [isLargeScreen]);
  const desktopLogo =
    brand === 'hlx' ? <HlxLogo height={32} /> : <McvLogo label="side" height={32} />;
  const mobileLogo =
    brand === 'hlx' ? <HlxLogo label="icon" height={32} /> : <McvLogo label="icon" height={32} />;

  // [MCV-1514] `fixed`, not `absolute`, so the nav stays visible however far the
  // page scrolls. Both are out of normal flow, so the `pt-20` clearance the two
  // mount points already reserve (PublicApp, and the SignedOut shell in app.tsx)
  // keeps working unchanged.
  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-white">
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
      >
        {/* HLX uses the wide "Healthcare Labor Exchange" lockup, so the logo
            column sizes to its content instead of an equal flex-1 split that
            would squeeze (and stretch) it; MCV's compact lockup keeps flex-1
            so its nav stays centered. [MCV-1487] */}
        <div className={brand === 'hlx' ? 'flex shrink-0' : 'flex lg:flex-1'}>
          {/* MCV-1513 brand switcher trigger, wrapping the existing logo */}
          <HeaderBrandMenu
            brand={brand}
            surface="public-header"
            primaryPath="/"
            primaryLabel="Go to home"
            triggerAriaLabel={`${siteName} menu`}
            triggerClassName="-m-1.5 p-1.5 block"
          >
            {desktopLogo}
          </HeaderBrandMenu>
        </div>
        <div className={`flex ${collapsedMenuClass}`}>
          <button
            data-cy={'collapsed-nav-menu'}
            type="button"
            aria-expanded={collapsedMenuOpen}
            onClick={() => setCollapsedMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-control p-2.5 text-grey-600"
          >
            <span className="sr-only">Open main menu</span>
            {!collapsedMenuOpen && <Bars3Icon aria-hidden="true" className="size-6" />}
          </button>
        </div>
        <div
          className={
            brand === 'hlx'
              ? // ml-8, not ml-12: the signed-in account area needs the 16px (see
                // AccountIdentity). Deliberately applied in EVERY auth state, so the tabs
                // sit 16px further left for anonymous visitors too; conditioning it on
                // `signed-in` would make the tabs jump when Clerk answers.
                'hidden xl:flex xl:gap-x-12 xl:ml-8'
              : 'hidden lg:flex lg:gap-x-6 lg:ml-8 xl:gap-x-12 xl:ml-12'
          }
        >
          {navigationItems.map((item) => (
            <Link
              aria-current={item.isActive ? 'page' : undefined}
              key={item.name}
              to={item.href}
              className={classNames(
                'shrink-0 whitespace-nowrap text-sm/6 font-semibold',
                item.isActive && activeLinkClass
              )}
            >
              {item.name}
            </Link>
          ))}
        </div>
        {isLargeScreen && (
          // `loading` keeps the slot empty so a signed-in visitor never sees "Log in" flash.
          <div className="flex lg:flex-1 lg:justify-end">
            {auth.status === 'signed-out' && (
              <Link
                to={paths.publicAuth.loginOrCreateAccount}
                data-cy={CT.Homepage.signinBtn}
                className="-mx-3 block whitespace-nowrap rounded-lg px-3 py-2 body-text hover:bg-grey-900"
              >
                Log in
              </Link>
            )}
            {auth.status === 'signed-in' && (
              <div className="flex items-center gap-x-4">
                <AccountIdentity auth={auth} brand={brand} />
                {/* Drawn as "Dashboard": "Go to dashboard" (~110px) would fill HLX's whole
                    slot and leave none of the slack the wider Linux/Windows tab labels need
                    (see AccountIdentity). The accessible name is the full phrase. */}
                <Link
                  to={paths.appHome}
                  data-cy={CT.Homepage.dashboardBtn}
                  className="-mx-3 block shrink-0 whitespace-nowrap rounded-lg px-3 py-2 body-text hover:bg-grey-900"
                >
                  <span className="sr-only">Go to</span>{' '}
                  {/* inline-block: Chrome will not capitalize a word that continues the
                      sr-only text before it. */}
                  <span className="inline-block capitalize">dashboard</span>
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>
      <Dialog
        open={collapsedMenuOpen}
        onClose={setCollapsedMenuOpen}
        aria-label="Main menu"
        className={collapsedMenuClass}
      >
        <div className="fixed inset-0 z-10" />
        <DialogPanel
          data-cy="public-nav-drawer"
          className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-grey-100 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-white/10"
        >
          <div className="flex items-center justify-between">
            <HeaderBrandMenu
              brand={brand}
              surface="public-header-drawer"
              // The drawer is a modal Dialog; portalling the panel out of it
              // would put the menu items outside the dialog's own focus scope.
              inline
              primaryPath="/"
              primaryLabel="Go to home"
              triggerAriaLabel={`${siteName} menu`}
              triggerClassName={
                brand === 'hlx' ? 'rounded bg-white p-1.5 block' : '-m-1.5 p-1.5 block'
              }
            >
              {mobileLogo}
            </HeaderBrandMenu>
            <button
              type="button"
              onClick={() => setCollapsedMenuOpen(false)}
              className="-m-2.5 rounded-control p-2.5 text-grey-600"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-grey-400/25">
              <div className="space-y-2 py-6">
                {navigationItems.map((item) => (
                  <Link
                    aria-current={item.isActive ? 'page' : undefined}
                    key={item.name}
                    to={item.href}
                    onClick={() => setCollapsedMenuOpen(false)}
                    className={classNames(
                      '-mx-3 block rounded-lg px-3 py-2 font-semibold text-white hover:bg-grey-200',
                      item.isActive && activeLinkClass
                    )}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              {auth.status === 'signed-out' && (
                <div className="flex flex-row py-6">
                  <Link
                    to={paths.publicAuth.loginOrCreateAccount}
                    onClick={() => setCollapsedMenuOpen(false)}
                    data-cy={CT.Homepage.signinBtn}
                    className="-mx-3 block rounded-lg px-3 py-2 font-semibold text-white hover:bg-grey-200"
                  >
                    Log in
                  </Link>
                  <span className="pl-6 self-center">Account & Settings </span>
                </div>
              )}
              {auth.status === 'signed-in' && (
                <div className="flex flex-row py-6">
                  <Link
                    to={paths.appHome}
                    onClick={() => setCollapsedMenuOpen(false)}
                    data-cy={CT.Homepage.dashboardBtn}
                    className="-mx-3 block shrink-0 rounded-lg px-3 py-2 font-semibold text-white hover:bg-grey-200"
                  >
                    Go to dashboard
                  </Link>
                  <span className="min-w-0 truncate pl-6 self-center text-sm text-white/70">
                    {auth.displayName ? `Signed in as ${auth.displayName}` : 'Signed in'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
};
